import { Component, HostListener, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DriverRecordRequestService } from '../../../services/driverrecordrequest.service';
import { IWizard } from '../../../interfaces/iwizard';
import { WizardSteps } from '../../../interfaces/wizard-steps';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';
import DriverLicense from '../../../interfaces/driverLicense';
import { GeneralService } from '../../../services/general.service';
import { Address } from '../../../interfaces/address';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslatorService } from '../../../services/translator.service';
import { UserProfile } from '../../../interfaces/UserProfile';
import { AuthenticationService } from '../../../services/authentication.service';

@Component({
    selector: 'app-license',
    templateUrl: './license.component.html',
    styleUrls: ['./license.component.scss'],
    standalone: false
})
export class LicenseComponent {

     form!: FormGroup;
     public wizard!: IWizard;
     userProfile?: UserProfile;

     get isReorder(): boolean {
          return this.userProfile?.userData?.roles.includes('driver') ?? false;
     }

     constructor (public service: DriverRecordRequestService,
          public generalService: GeneralService,
          public _router: Router,
          private _modalService: NgbModal,
          public translatorService: TranslatorService,
          public authenticationService: AuthenticationService,
          private route: ActivatedRoute,
          public translationService: TranslatorService,) {

          this.wizard = this.service.getWizard();
          this.gridLanguage = this.translatorService.getActiveLanguage().toUpperCase();
          this.setNoRowsText();
     }

     stateList: any = [];
     licenseState: string = '';
     licenseLastName: string = '';
     licenseNumber: string = '';
     licenses: DriverLicense[] = [];
     duplicateWasEntered: boolean = false;
     existingEmail: string = '';
     isVerifying: boolean = false;
     isValid: boolean = true;
     newLicense: DriverLicense = { lastName: '', state: '', licenseNumber: '' };
     isNewLicenseNumber = true;
     isNewLicenseName = true;
     stateIsDirty = false;
     noRowsText = '';
     gridLanguage: string = '';
     address?: Address;
     routeParams: ParamMap = {} as ParamMap;

     @ViewChild('existingDriver')
     existingDriverModal!: TemplateRef<any>;

     ngOnInit(): void {
          setTimeout(() => this.scrollToTop(), 500);

          this.userProfile = this.authenticationService.getUserProfile();
          this.routeParams = this.route.snapshot.queryParamMap;

          if (this.service.isDriverRequestInitialized === false && this.service.loadFromLocal() === false) {
               this._router.navigateByUrl('/driver/1');
          }

          // Define inputs with validations 
          this.form = new FormGroup({
               licenseLastName: new FormControl(this.licenseLastName, [
                    Validators.required,
                    Validators.maxLength(30),
                    Validators.pattern(/^([a-zA-Z]*)$/)
               ]),
               licenseNumber: new FormControl(this.licenseNumber, [
                    Validators.maxLength(20),
                    Validators.pattern(/^([a-zA-Z0-9]*)$/)
               ]),
               licenseState: new FormControl(this.licenseState, [
                    Validators.required
               ])
          });

          const statesPromise = this.generalService.getStatesByCountry('USA');
          statesPromise.then((v: any) => this.loadStates(v));

          if (this.service.driverRequest.driversLicenses) {
               this.licenses = this.service.driverRequest.driversLicenses;
          }

          if (this.service.driverRequest.address) {
               this.address = this.service.driverRequest.address;
          }

          if (this.routeParams && this.routeParams.has('dupLic') && this.routeParams.get('dupLic')?.toString()) {
               this.doesAlreadyLicenseExist(true);
               const existingUsernamePromise = this.service.getEmailForLicenseId(this.routeParams.get('dupLic')?.toString() ?? '');
               existingUsernamePromise.then((v: string) => this.populateExistingUserEmail(v));
          }
     }

     get lastName() { return this.form!.get('licenseLastName'); }
     get dlNumber() { return this.form!.get('licenseNumber'); }
     get state() { return this.form!.get('licenseState'); }

     ngOnDestroy() {
          this.service.driverRequest.driversLicenses = this.licenses;
          this.service.saveToLocal();
     }

     loadStates(value: string[]) {
          this.stateList = value;
     }

     licenseNumberChanged() {
          this.isNewLicenseNumber = false;
     }

     licenseNameChanged() {
          this.isNewLicenseName = false;
     }

     addLicense() {
          this.isVerifying = true;
          this.duplicateWasEntered = false;
          this.isValid = true;

          if (this.lastName?.value && this.lastName?.value.length > 0 &&
               this.dlNumber?.value && this.dlNumber?.value.length > 0 &&
               this.state?.value && this.state?.value.length > 0) {
               this.newLicense = {
                    lastName: this.lastName.value.toLocaleUpperCase(),
                    licenseNumber: this.dlNumber.value.toLocaleUpperCase(),
                    state: this.state.value.toLocaleUpperCase()
               };
               this.verifyLicense();
          } else {
               this.isVerifying = false;
          }
          document.getElementById('lastName')?.focus();
     }

     verifyLicense() {
          const localDuplicates = this.licenses.filter(dl => dl.lastName === this.newLicense.lastName &&
               dl.licenseNumber === this.newLicense.licenseNumber &&
               dl.state === this.newLicense.state);

          if (localDuplicates && localDuplicates.length > 0) {
               this.duplicateWasEntered = true;
               this.isValid = false;
               this.resetNewLicense();
               this.isVerifying = false;
          } else {
               // Not a local dup. Check for server dup.
               const licenseExistPromise = this.service.doesLicenseExist(this.newLicense);
               licenseExistPromise.then((v: boolean) => this.doesAlreadyLicenseExist(v))
          }
     }

     doesAlreadyLicenseExist(licenseExists: boolean) {

          if (licenseExists) {
               const existingUsernamePromise = this.service.getEmailForLicense(this.newLicense);
               existingUsernamePromise.then((v: string) => this.populateExistingUserEmail(v));
          } else {
               this.existingEmail = '';
               this.isVerifying = false;
               this.isValid = true;
               this.licenses.push(this.newLicense);

               this.resetNewLicense();
          }
     }

     populateExistingUserEmail(existingUserEmail: string) {
          this.existingEmail = this.generalService.maskEmail(existingUserEmail);
          this.isValid = false;
          this.isVerifying = false;
          this._modalService.open(this.existingDriverModal, { ariaLabelledBy: 'existingDriverTitle' });
     }

     dismissExistingDriverModal() {
          this._modalService.dismissAll();
          this.clearDuplicateLicense();
          this.scrollToTop();
     }

     removeLicense(license: DriverLicense) {
          this.licenses = this.licenses.filter(function (dl: DriverLicense, index, array: DriverLicense[]) {
               return dl != license;
          });
     }

     get hasErrors(): boolean {
          const hasErrors = this.licenses.length === 0;
          if (!hasErrors) {
               this.service.driverRequest.driversLicenses = this.licenses;
          }
          return hasErrors;
     }

     get licenseHasErrors(): boolean {
          const isValid = this.lastName?.value && this.lastName.value.length > 0 &&
               this.dlNumber?.value && this.dlNumber.value.length > 0 &&
               this.state?.value && this.state.value.length > 0;

          return !isValid;
     }

     clearDuplicateLicense() {
          this.existingEmail = '';
          this.resetNewLicense();
     }

     resetNewLicense() {
          this.form!.reset();
          this.newLicense = { lastName: '', state: '', licenseNumber: '' };
          this.isNewLicenseNumber = true;
          this.isNewLicenseName = true;
          this.stateIsDirty = false;
          this.existingEmail = '';
     }

     customizeAlreadyExistsMessage() {
          let translatedText: string = this.generalService.getTranslation('ExistingLicense', 'base')
          if (translatedText === undefined) {
               translatedText = this.generalService.getTranslation('ExistingLicense', 'base');
          }
          translatedText = translatedText.replace('##email##', this.existingEmail);
          return translatedText;
     }

     scrollToTop() {
          window.scrollTo(0, 0);
     }

     @HostListener('window:beforeunload', ['$event'])
     unloadMessage($event: { returnValue: string; }) {
          return $event.returnValue = 'Your changes will not be saved';
     }

     private setNoRowsText() {
          if (this.gridLanguage === 'ES'){
               this.noRowsText = 'No se han encontrado resultados';
          } else if (this.gridLanguage === 'FR') {
               this.noRowsText = 'Aucun résultat trouvé';
          } else {
               this.noRowsText = 'No results found';
          }
     }

     translate(translationTag: string) { 
          const lang = this.translationService.getActiveLanguage().toUpperCase();
          if (translationTag === 'Voodoo' && this.gridLanguage !== lang as string) {
               this.gridLanguage = lang;
               this.setNoRowsText();
          }
     }
}
